import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useBetaData } from '../../contexts/BetaDataContext';
import { MAIN_SYSTEM_URL } from '../../config';
import { supabase } from '../../supabaseClient';
import NotificationBell from './notifications/NotificationBell';
import AuditLogViewer from '../auditLog/AuditLogViewer';
import AutopilotProjectPanel from './AutopilotProjectPanel';
import { SharedAppSwitcher, SharedAccountMenu } from '../common/SharedUI';
import { VERSION } from '../../version';
import { isNewWorkItemUI, isAfterCutover } from '../../constants/cutover';
import './BetaTopBar.scss';

const BetaTopBar = ({ currentView, selectedProjectId, onMenuClick, onNavigate, onRequestCreateWorkItem }) => {
  const { profile, signOut, refreshProfile } = useAuth();
  const { projects } = useBetaData();
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [showAutopilot, setShowAutopilot] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const isNewUI = isNewWorkItemUI(searchParams);
  const cutoverPassed = isAfterCutover();

  const currentProject = currentView === 'project' ? (projects || []).find((p) => p.id === selectedProjectId) : null;
  const autopilotEnabled = !!currentProject?.autopilot_config?.gitlabProjectPath;

  // 舊版 / 新版工作項目 切換:預設新版,切舊版設 ?v=old,切回新版移除參數
  const toggleNewUI = () => {
    setSearchParams((prev) => {
      if (isNewWorkItemUI(prev)) prev.set('v', 'old');
      else prev.delete('v');
      return prev;
    });
  };

  const openAuditLogModal = () => setShowAuditLog(true);

  const getViewTitle = () => {
    if (isNewUI) {
      if (currentView === 'my-tasks') return '我的工作項目';
      if (currentView === 'global-search') return '全系統工作項目查詢';
      if (currentView === 'global-ticket-search') return '全系統 Ticket 查詢';
      const proj = (projects || []).find((p) => p.id === selectedProjectId);
      return proj ? proj.name : '工作項目';
    }
    switch (currentView) {
      case 'my-tasks': return '我的任務';
      case 'project': {
        const proj = (projects || []).find((p) => p.id === selectedProjectId);
        return proj?.name || '專案';
      }
      case 'ticket': return 'Ticket';
      case 'timeline': return 'Timeline';
      case 'board': return 'Board';
      case 'milestone': return 'Milestone';
      default: return '我的任務';
    }
  };

  return (
    <div className="beta-topbar">
      {/* Mobile hamburger */}
      <button className="beta-topbar-menu" onClick={onMenuClick}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 5h14M3 10h14M3 15h14" strokeLinecap="round" />
        </svg>
      </button>

      {/* View title */}
      <h2 className="beta-topbar-title">{getViewTitle()}</h2>
      {autopilotEnabled && (
        <button
          className="topbar-btn"
          title="Autopilot:AI 修復歷史 / 部署"
          onClick={() => setShowAutopilot(true)}
        >
          🚀
        </button>
      )}

      {/* Actions */}
      <div className="beta-topbar-actions">
        {/* 舊版 / 新版工作項目 切換開關(轉換期限前顯示;過期後統一新版,隱藏開關)*/}
        {!cutoverPassed && (
        <button
          type="button"
          role="switch"
          aria-checked={isNewUI}
          className={`ui-mode-switch ${isNewUI ? 'on' : ''}`}
          onClick={toggleNewUI}
          title={isNewUI ? '切回舊版' : '切換到新版工作項目'}
        >
          <span className="ums-ghost ums-ghost-new" aria-hidden="true">新版</span>
          <span className="ums-ghost ums-ghost-old" aria-hidden="true">舊版</span>
          <span className="ums-star s1" aria-hidden="true" />
          <span className="ums-star s2" aria-hidden="true" />
          <span className="ums-star s3" aria-hidden="true" />
          <span className="ums-star s4" aria-hidden="true" />
          <span className="ums-knob">
            <span className="ums-kw ums-kw-old">舊版</span>
            <span className="ums-kw ums-kw-new">新版</span>
          </span>
        </button>
        )}

        {/* Search button (mobile) */}
        <button className="topbar-btn mobile-only" title="搜尋">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="8" cy="8" r="5" />
            <path d="M12 12l4 4" strokeLinecap="round" />
          </svg>
        </button>

        {/* Daily report button */}
        <button
          className="topbar-btn create-btn daily-report-btn"
          title="Daily Report"
          data-tour="daily-report"
          onClick={() => onNavigate('daily-report')}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="2" y="3" width="14" height="13" rx="2" />
            <path d="M2 7h14M6 1v4M12 1v4" strokeLinecap="round" />
          </svg>
          <span className="btn-label">Daily Report</span>
        </button>

        {/* Create button — 全面封鎖建立 Ticket:舊版 mode 不再顯示此鈕,新版照常「新增工作項目」 */}
        {/* 原(舊版可建 Ticket)版本:
        <button
          className="topbar-btn create-btn"
          onClick={() => (isNewUI ? onRequestCreateWorkItem?.() : onNavigate('create-ticket'))}
          title={isNewUI ? '新增工作項目' : '建立 Ticket'}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 3v12M3 9h12" strokeLinecap="round" />
          </svg>
          <span className="btn-label">{isNewUI ? '新增工作項目' : '建立 Ticket'}</span>
        </button>
        */}
        {isNewUI && (
        <button
          className="topbar-btn create-btn"
          onClick={() => onRequestCreateWorkItem?.()}
          title="新增工作項目"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 3v12M3 9h12" strokeLinecap="round" />
          </svg>
          <span className="btn-label">新增工作項目</span>
        </button>
        )}

        {/* Notification bell */}
        <NotificationBell onNavigate={onNavigate} />

        {/* Shared UI — App Switcher & Account Menu */}
        <SharedAppSwitcher currentSystem="project" mainSystemUrl={MAIN_SYSTEM_URL} />
        <SharedAccountMenu
          user={profile ? {
            id: profile.id,
            email: profile.email,
            name: profile.name,
            avatar_url: profile.avatar_url,
            role: profile.role,
          } : null}
          version={VERSION}
          mainSystemUrl={MAIN_SYSTEM_URL}
          customMenuItems={[
            { label: '操作記錄', icon: '📋', onClick: openAuditLogModal, roles: ['super_admin', 'company_admin', 'department_admin'] },
            { label: '匯出我的資料', icon: '📤', onClick: () => onNavigate('export') },
            ...(isNewUI ? [{ label: '重新觀看導覽', icon: '🎯', onClick: () => onNavigate('replay-wi-tour') }] : []),
          ]}
          onAvatarChange={async (blob) => {
            const path = `${profile.id}/avatar.webp`
            const { error: uploadError } = await supabase.storage
              .from('avatars')
              .upload(path, blob, { upsert: true, contentType: 'image/webp' })
            if (uploadError) throw uploadError
            const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(path)
            const newUrl = `${urlData.publicUrl}?t=${Date.now()}`
            await supabase.from('profiles').update({ avatar_url: newUrl }).eq('id', profile.id)
            refreshProfile()
          }}
          onLogout={signOut}
        />
      </div>

      {/* Audit Log Modal */}
      {showAuditLog && (
        <AuditLogViewer
          isOpen={showAuditLog}
          onClose={() => setShowAuditLog(false)}
        />
      )}

      {/* Autopilot Modal */}
      {showAutopilot && (
        <AutopilotProjectPanel
          isOpen={showAutopilot}
          onClose={() => setShowAutopilot(false)}
          project={currentProject}
        />
      )}
    </div>
  );
};

export default BetaTopBar;
